/**
 * ReminderTemplatesListController — page /relances-modeles.html
 * (EPIC-004 / 4.7).
 *
 * Hydrate la table des templates depuis `GET /invoice-reminder-templates`,
 * et permet l'archivage (soft-delete) inline avec confirmation.
 *
 * Le tableau de la maquette présente les templates actifs ordonnés par
 * `delayDays` ASC. Chaque ligne expose le titre B2B impayée (variante
 * pivot affichée côté liste, les 3 autres restant éditables côté
 * /relances-modeles-edition.html).
 *
 * Convention CLAUDE.md respectée :
 *   - Pas d'HTML inline en JS : la ligne et les états vide/erreur sont
 *     rendus depuis `<template>` (`tpl-invoice-reminder-template-row`,
 *     `tpl-invoice-reminder-template-empty`, `tpl-invoice-reminder-template-error`).
 *   - Aucune classe CSS inventée — seulement des classes ⊆ notamicro.css.
 *   - Vocabulaire UI : aucun nom de prestataire visible (relances internes
 *     Notamicro). Termes neutres « template », « variante », « délai ».
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

export default class ReminderTemplatesListController extends BaseController {
  static targets = [
    "tableBody",
    "loadingRow",
    "newButton",
    // EPIC-099.23 — toggle vue archivés via `?archived=1`.
    "defaultHeading",
    "archivedHeading",
    "defaultSub",
    "archivedSub",
    "defaultIntro",
    "archivedIntro",
    "defaultFootnote",
    "backToActiveButton",
    "viewArchivedRow",
    "viewArchivedButton",
  ];

  // EPIC-099.23 — mode archivés activé par `?archived=1`. Bascule deux blocs :
  //  - le H1, sous-titre, intro et footnote alternent entre deux variantes ;
  //  - les boutons primaires/secondaires inversent leur visibilité ;
  //  - le backend reçoit `?include_archived=1` puis on filtre côté JS sur
  //    `status === 'archived'` pour n'afficher que les archivés.
  _archivedMode = false;

  async connect() {
    const params = new URLSearchParams(window.location.search);
    this._archivedMode = params.get("archived") === "1";
    this._applyModeChrome();

    try {
      const data = await api.listReminderTemplates({
        includeArchived: this._archivedMode,
      });
      const all = Array.isArray(data?.templates) ? data.templates : [];
      const displayed = this._archivedMode
        ? all.filter((t) => t?.status === "archived")
        : all;
      this._renderTemplates(displayed);
    } catch (error) {
      // 401 déjà redirigé par api.js. Pour toute autre erreur, on affiche
      // l'état d'erreur dans la table.
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les templates de relance.";
      this.showError(msg);
    }
  }

  // EPIC-099.23 — bascule les éléments d'en-tête / bas de page selon le mode.
  _applyModeChrome() {
    const toggle = (target, hidden) => {
      if (target) {
        target.hidden = hidden;
      }
    };

    toggle(this.hasDefaultHeadingTarget ? this.defaultHeadingTarget : null, this._archivedMode);
    toggle(this.hasArchivedHeadingTarget ? this.archivedHeadingTarget : null, !this._archivedMode);
    toggle(this.hasDefaultSubTarget ? this.defaultSubTarget : null, this._archivedMode);
    toggle(this.hasArchivedSubTarget ? this.archivedSubTarget : null, !this._archivedMode);
    toggle(this.hasDefaultIntroTarget ? this.defaultIntroTarget : null, this._archivedMode);
    toggle(this.hasArchivedIntroTarget ? this.archivedIntroTarget : null, !this._archivedMode);
    toggle(this.hasDefaultFootnoteTarget ? this.defaultFootnoteTarget : null, this._archivedMode);
    toggle(this.hasNewButtonTarget ? this.newButtonTarget : null, this._archivedMode);
    toggle(this.hasBackToActiveButtonTarget ? this.backToActiveButtonTarget : null, !this._archivedMode);
    toggle(this.hasViewArchivedRowTarget ? this.viewArchivedRowTarget : null, this._archivedMode);
  }

  // ---------------------------------------------------------------------------
  // Archive (soft-delete)
  // ---------------------------------------------------------------------------

  async archiveTemplate(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const templateId = button.getAttribute("data-id");
    if (!templateId) return;

    const ok = await this.confirm(
      "Archiver ce template ? Il ne sera plus utilisé pour les relances "
      + "automatiques. Vous pourrez en créer un nouveau pour le même délai.",
      { confirmLabel: "Archiver", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    button.disabled = true;
    try {
      await api.archiveReminderTemplate(templateId);
      this.showSuccess("Template archivé.");
      // Re-fetch pour rester cohérent avec la limite 7 (et libérer le délai).
      const data = await api.listReminderTemplates();
      this._renderTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Erreur lors de l'archivage.";
      this.showError(msg);
      button.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  _renderTemplates(templates) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (templates.length === 0) {
      const tplId = this._archivedMode
        ? "tpl-invoice-reminder-template-empty-archived"
        : "tpl-invoice-reminder-template-empty";
      const empty = this.cloneTemplate(tplId);
      this.tableBodyTarget.appendChild(empty);
      return;
    }

    for (const tpl of templates) {
      const fragment = this.cloneTemplate("tpl-invoice-reminder-template-row");
      this.fillSlots(fragment, {
        delay: this._formatDelay(tpl.delayDays),
        title: tpl.titleImpayeB2b || "—",
      });
      const editLink = fragment.querySelector('[data-slot="editHref"]');
      if (editLink) {
        editLink.setAttribute("href", `/relances-modeles-edition.html?id=${encodeURIComponent(tpl.id)}`);
      }
      const archiveBtn = fragment.querySelector('[data-slot="archiveButton"]');
      if (archiveBtn) {
        archiveBtn.setAttribute("data-id", tpl.id);
      }
      // EPIC-099.23 — pill et bouton Archiver conditionnels selon le statut
      // (le backend renvoie actifs + archivés en mode archivés ; le filtrage
      // de la vue est déjà fait dans `connect()`, on reste défensif ici).
      const isArchived = tpl?.status === "archived";
      if (isArchived) {
        const pill = fragment.querySelector('[data-slot="statusPill"]');
        if (pill) {
          pill.classList.remove("nm-pill--olive");
          pill.classList.add("nm-pill--muted");
        }
        const label = fragment.querySelector('[data-slot="statusLabel"]');
        if (label) {
          label.textContent = "archivé";
        }
        if (archiveBtn) {
          archiveBtn.hidden = true;
        }
      }
      this.tableBodyTarget.appendChild(fragment);
    }

    // EPIC-099.23 — la limite 7 ne concerne que les actifs. En mode archivés,
    // le bouton « + Nouveau modèle » est masqué de toute façon (cf.
    // _applyModeChrome), donc on n'a rien à signaler.
    if (!this._archivedMode && this.hasNewButtonTarget) {
      if (templates.length >= 7) {
        this.newButtonTarget.setAttribute("title", "Limite de 7 templates atteinte. Archivez un template avant d'en créer un nouveau.");
      } else {
        this.newButtonTarget.removeAttribute("title");
      }
    }
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    const fragment = this.cloneTemplate("tpl-invoice-reminder-template-error");
    this.tableBodyTarget.appendChild(fragment);
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _formatDelay(delayDays) {
    const n = Number(delayDays);
    if (!Number.isFinite(n)) return "—";
    return `J+${n}`;
  }

}
